import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Building2, Hospital, GraduationCap, Landmark, ShoppingBag, Factory, Plane, Shield, Cloud, Database, Network, Code, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
interface SolutionsProps {
  onBack?: () => void;
}
const solutions = [{
  id: 1,
  icon: Building2,
  title: 'Enterprise Solutions',
  titleAr: 'حلول المؤسسات',
  description: 'Comprehensive IT infrastructure for large organizations',
  features: ['Enterprise Resource Planning (ERP)', 'Customer Relationship Management (CRM)', 'Business Intelligence & Analytics', 'Digital Workplace Solutions'],
  benefits: 'Streamlined operations, improved productivity, data-driven decisions',
  color: '#007A3D'
}, {
  id: 2,
  icon: Cloud,
  title: 'Cloud Transformation',
  titleAr: 'التحول السحابي',
  description: 'Migrate and optimize your infrastructure in the cloud',
  features: ['Cloud Migration Strategy', 'Multi-Cloud Management', 'Cloud Cost Optimization', 'Cloud Security & Compliance'],
  benefits: 'Reduced costs, enhanced scalability, improved business agility',
  color: '#3498DB'
}, {
  id: 3,
  icon: Shield,
  title: 'Cybersecurity Suite',
  titleAr: 'مجموعة الأمن السيبراني',
  description: 'Comprehensive security solutions for your organization',
  features: ['Security Operations Center (SOC)', 'Threat Intelligence & Monitoring', 'Incident Response', 'Security Awareness Training'],
  benefits: 'Protected assets, compliance assurance, reduced risk exposure',
  color: '#D7182A'
}, {
  id: 4,
  icon: Database,
  title: 'Data Management',
  titleAr: 'إدارة البيانات',
  description: 'Advanced data solutions for intelligent insights',
  features: ['Big Data Analytics', 'Data Warehousing', 'Master Data Management', 'Data Governance Framework'],
  benefits: 'Better insights, data quality, regulatory compliance',
  color: '#9B59B6'
}, {
  id: 5,
  icon: Network,
  title: 'Network Infrastructure',
  titleAr: 'البنية التحتية للشبكة',
  description: 'Robust and scalable network solutions',
  features: ['Network Design & Implementation', 'SD-WAN Solutions', 'Network Security', '24/7 Network Monitoring'],
  benefits: 'Reliable connectivity, enhanced performance, reduced downtime',
  color: '#1ABC9C'
}, {
  id: 6,
  icon: Code,
  title: 'Custom Development',
  titleAr: 'التطوير المخصص',
  description: 'Tailored software solutions for unique business needs',
  features: ['Web & Mobile Applications', 'API Development & Integration', 'Legacy System Modernization', 'Microservices Architecture'],
  benefits: 'Competitive advantage, streamlined processes, innovation',
  color: '#E67E22'
}];
const industries = [{
  icon: Landmark,
  name: 'Government',
  nameAr: 'الحكومة'
}, {
  icon: Hospital,
  name: 'Healthcare',
  nameAr: 'الرعاية الصحية'
}, {
  icon: GraduationCap,
  name: 'Education',
  nameAr: 'التعليم'
}, {
  icon: ShoppingBag,
  name: 'Retail',
  nameAr: 'التجزئة'
}, {
  icon: Factory,
  name: 'Manufacturing',
  nameAr: 'التصنيع'
}, {
  icon: Plane,
  name: 'Transportation',
  nameAr: 'النقل'
}];
const caseStudies = [{
  title: 'Ministry Digital Transformation',
  industry: 'Government',
  challenge: 'Legacy systems causing inefficiencies',
  solution: 'Cloud migration and modern architecture',
  results: '60% cost reduction, 3x faster processing',
  color: '#007A3D'
}, {
  title: 'Hospital Security Enhancement',
  industry: 'Healthcare',
  challenge: 'Patient data security concerns',
  solution: 'Comprehensive cybersecurity implementation',
  results: '100% compliance, zero security incidents',
  color: '#D7182A'
}, {
  title: 'University Cloud Infrastructure',
  industry: 'Education',
  challenge: 'Scalability for growing student population',
  solution: 'Multi-cloud architecture with automation',
  results: '90% improved uptime, 40% cost savings',
  color: '#3498DB'
}];
export default function Solutions({
  onBack
}: SolutionsProps) {
  const [selectedSolution, setSelectedSolution] = useState<typeof solutions[0] | null>(null);
  return <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#007A3D] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-[#007A3D] to-[#005B2A] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Our Solutions
            </h1>
            <p className="text-xl text-white/90 mb-6">
              حلولنا التقنية
            </p>
            <p className="text-lg text-white/80">
              Comprehensive technology solutions designed to transform your business 
              and drive sustainable growth in the digital age.
            </p>
          </motion.div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48h1440V0c-240 48-480 48-720 24C480 0 240 0 0 24v24z" fill="#F9FAFB" />
          </svg>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => <motion.div key={solution.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all cursor-pointer group" onClick={() => setSelectedSolution(solution)}>
                <div className="h-2 w-full rounded-t-2xl" style={{
              backgroundColor: solution.color
            }} />
                
                <div className="p-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{
                backgroundColor: `${solution.color}20`
              }}>
                    <solution.icon style={{
                  color: solution.color
                }} size={28} />
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {solution.title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">{solution.titleAr}</p>
                  <p className="text-gray-600 mb-4">{solution.description}</p>

                  <div className="space-y-2 mb-4">
                    {solution.features.slice(0, 3).map((feature, i) => <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle size={16} style={{
                    color: solution.color
                  }} />
                        <span>{feature}</span>
                      </div>)}
                  </div>

                  <button className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors" style={{
                backgroundColor: `${solution.color}10`,
                color: solution.color
              }}>
                    Learn More
                    <ArrowRight size={18} />
                  </button>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Industries We Serve
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Tailored solutions for various sectors across the region
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {industries.map((industry, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-gray-50 p-6 rounded-2xl text-center hover:bg-[#007A3D] hover:text-white transition-all cursor-pointer group">
                <industry.icon className="mx-auto mb-3 text-[#007A3D] group-hover:text-white transition-colors" size={32} />
                <h3 className="font-semibold text-gray-800 group-hover:text-white mb-1">
                  {industry.name}
                </h3>
                <p className="text-xs text-gray-500 group-hover:text-white/80">
                  {industry.nameAr}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Success Stories
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real results from our client partnerships
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
                <div className="h-2 w-full" style={{
              backgroundColor: study.color
            }} />
                
                <div className="p-6">
                  <div className="text-sm font-medium mb-2" style={{
                color: study.color
              }}>
                    {study.industry}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    {study.title}
                  </h3>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Challenge</p>
                      <p className="text-sm text-gray-700">{study.challenge}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Solution</p>
                      <p className="text-sm text-gray-700">{study.solution}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Results</p>
                      <p className="text-sm font-semibold" style={{
                    color: study.color
                  }}>
                        {study.results}
                      </p>
                    </div>
                  </div>

                  <button className="text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all" style={{
                color: study.color
              }}>
                    Read Full Case Study
                    <ArrowRight size={16} />
                  </button>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Implementation Process
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A proven methodology for successful solution delivery
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[{
            step: '01',
            title: 'Discovery',
            description: 'Understanding your business requirements and challenges'
          }, {
            step: '02',
            title: 'Design',
            description: 'Creating tailored solution architecture and roadmap'
          }, {
            step: '03',
            title: 'Implementation',
            description: 'Deploying solutions with minimal business disruption'
          }, {
            step: '04',
            title: 'Support',
            description: 'Ongoing optimization and technical assistance'
          }].map((phase, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="text-center">
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#007A3D] to-[#005B2A] rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4">
                  {phase.step}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {phase.title}
                </h3>
                <p className="text-gray-600">{phase.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#007A3D] to-[#005B2A] text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Let's discuss how our solutions can address your specific challenges
            </p>
            <button className="bg-[#D7182A] hover:bg-[#b51525] text-white px-8 py-4 rounded-full font-semibold transition-colors inline-flex items-center gap-2" onClick={onBack}>
              Schedule a Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Solution Detail Modal */}
      {selectedSolution && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSolution(null)}>
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="h-3 w-full rounded-t-2xl" style={{
          backgroundColor: selectedSolution.color
        }} />
            
            <div className="p-8">
              <button onClick={() => setSelectedSolution(null)} className="text-gray-400 hover:text-gray-600 mb-4 float-right">
                ✕ Close
              </button>

              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{
            backgroundColor: `${selectedSolution.color}20`
          }}>
                <selectedSolution.icon style={{
              color: selectedSolution.color
            }} size={36} />
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {selectedSolution.title}
              </h2>
              <p className="text-lg text-gray-500 mb-6">{selectedSolution.titleAr}</p>

              <p className="text-gray-700 mb-6">{selectedSolution.description}</p>

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Key Features</h3>
                <div className="space-y-3">
                  {selectedSolution.features.map((feature, i) => <div key={i} className="flex items-start gap-3">
                      <CheckCircle size={20} style={{
                  color: selectedSolution.color
                }} className="flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>)}
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-2">Benefits</h3>
                <p className="text-gray-700">{selectedSolution.benefits}</p>
              </div>

              <button className="w-full py-4 rounded-full font-semibold text-white transition-colors flex items-center justify-center gap-2" style={{
            backgroundColor: selectedSolution.color
          }} onClick={() => {
            setSelectedSolution(null);
            onBack?.();
          }}>
                Request a Demo
                <ArrowRight size={20} />
              </button>
            </div>
          </motion.div>
        </div>}
    </div>;
}